import React, { useMemo } from "react";
import { X } from "lucide-react";
import DatePickerUS from "@/components/DatePickerUS";

const RANGE_PILLS = [
    { value: "all", label: "All" },
    { value: "7", label: "Last 7" },
    { value: "15", label: "Last 15" },
    { value: "30", label: "Last 30" },
    { value: "custom", label: "Custom" },
];

const STATUS_PILLS = [
    { value: "all", label: "All", key: "total" },
    { value: "current", label: "Current", key: "current" },
    { value: "backlog", label: "Backlog", key: "backlog" },
];

const Pill = ({ active, onClick, children, testid }) => (
    <button
        type="button"
        onClick={onClick}
        data-testid={testid}
        data-active={active || undefined}
        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
            active
                ? "bg-brand-blue-700 text-white shadow-sm"
                : "bg-white text-slate-600 hover:bg-slate-100 border border-slate-200"
        }`}
    >
        {children}
    </button>
);

const Row = ({ label, children }) => (
    <div className="flex flex-wrap items-center gap-2">
        <span className="w-full text-[11px] font-semibold uppercase tracking-widest text-slate-400 sm:w-24">
            {label}
        </span>
        <div className="flex flex-wrap items-center gap-2">{children}</div>
    </div>
);

const HistoryFilters = ({
    range,
    dateFrom,
    dateTo,
    individualId,
    caregiverId,
    statusFilter,
    counts,
    individuals,
    caregivers,
    showCaregiverFilter,
    onRangeChange,
    onDateFromChange,
    onDateToChange,
    onIndividualChange,
    onCaregiverChange,
    onStatusChange,
    onClear,
}) => {
    const dateWarning = useMemo(() => {
        if (range !== "custom") return null;
        if (!dateFrom || !dateTo) return "Select both start and end dates.";
        if (dateFrom > dateTo) return "Start date must be before end date.";
        const df = new Date(dateFrom);
        const dt = new Date(dateTo);
        const days = Math.round((dt - df) / (1000 * 60 * 60 * 24));
        if (days > 90) return "Custom range cannot exceed 90 days.";
        return null;
    }, [range, dateFrom, dateTo]);

    const c = counts || { total: 0, current: 0, backlog: 0 };

    return (
        <div
            data-testid="history-filters"
            className="sticky top-16 z-[5] rounded-2xl border border-slate-200 bg-white/85 p-4 backdrop-blur-md shadow-sm"
        >
            <div className="flex items-start justify-between gap-3">
                <div className="flex-1 space-y-3">
                    {/* Range */}
                    <Row label="Range">
                        {RANGE_PILLS.map((p) => (
                            <Pill
                                key={p.value}
                                active={range === p.value}
                                onClick={() => onRangeChange(p.value)}
                                testid={`history-filter-range-${p.value}`}
                            >
                                {p.label}
                            </Pill>
                        ))}
                        {range === "custom" && (
                            <div className="ml-1 flex flex-wrap items-center gap-2">
                                <DatePickerUS
                                    value={dateFrom || ""}
                                    onChange={onDateFromChange}
                                    testId="history-filter-custom-from"
                                />
                                <span className="text-xs text-slate-400">to</span>
                                <DatePickerUS
                                    value={dateTo || ""}
                                    onChange={onDateToChange}
                                    testId="history-filter-custom-to"
                                />
                            </div>
                        )}
                        {dateWarning && (
                            <span className="text-xs font-semibold text-red-600">
                                {dateWarning}
                            </span>
                        )}
                    </Row>

                    {/* Caregiver (supervisor+ only, when 2+ caregivers) */}
                    {showCaregiverFilter && caregivers && caregivers.length > 1 && (
                        <Row label="Caregiver">
                            <Pill
                                active={!caregiverId}
                                onClick={() => onCaregiverChange(null)}
                                testid="history-filter-caregiver-all"
                            >
                                All ({caregivers.length})
                            </Pill>
                            {caregivers.map((c) => (
                                <Pill
                                    key={c.id}
                                    active={caregiverId === c.id}
                                    onClick={() => onCaregiverChange(c.id)}
                                    testid={`history-filter-caregiver-${c.id}`}
                                >
                                    {c.first_name} {c.last_name}
                                </Pill>
                            ))}
                        </Row>
                    )}

                    {/* Individual */}
                    {individuals && individuals.length > 1 && (
                        <Row label="Individual">
                            <Pill
                                active={!individualId}
                                onClick={() => onIndividualChange(null)}
                                testid="history-filter-individual-all"
                            >
                                All ({individuals.length})
                            </Pill>
                            {individuals.map((i) => (
                                <Pill
                                    key={i.id}
                                    active={individualId === i.id}
                                    onClick={() => onIndividualChange(i.id)}
                                    testid={`history-filter-individual-${i.id}`}
                                >
                                    {i.first_name} {i.last_name}
                                </Pill>
                            ))}
                        </Row>
                    )}

                    {/* Status */}
                    <Row label="Status">
                        {STATUS_PILLS.map((s) => (
                            <Pill
                                key={s.value}
                                active={statusFilter === s.value}
                                onClick={() => onStatusChange(s.value)}
                                testid={`history-filter-status-${s.value}`}
                            >
                                {s.label}
                                <span className="ml-1 rounded-full bg-white/20 px-1.5 py-0.5 text-[10px] tabular-nums">
                                    {c[s.key] ?? 0}
                                </span>
                            </Pill>
                        ))}
                    </Row>
                </div>
                <button
                    type="button"
                    onClick={onClear}
                    data-testid="history-filters-clear"
                    className="shrink-0 rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                    title="Clear all filters"
                    aria-label="Clear all filters"
                >
                    <X className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
};

export default HistoryFilters;
